<template>
  <div
    class="is-flex is-justify-content-space-between is-align-items-center my-3"
  >
    <!-- START TIME -->
    <b-field
      style="min-width: 300px"
      class="is-flex is-align-items-center my-0"
    >
      <b-button
        type="is-static"
        :label="`Round ${round.roundNumber}`"
        style="width: 100px; border: none"
      />
      <b-timepicker
        style="width: 150px"
        icon="clock"
        editable
        :disabled="!!round.finishTime"
        :value="round.startTime.toDate()"
        :min-time="minStartTime"
        :max-time="nextRound ? nextRound.startTime.toDate() : null"
        :ref="`startTime${round.roundNumber}`"
        @input="(e) => setRoundStartTime(e)"
      >
        <b-button
          type="is-danger"
          :label="`${-pushStep}m`"
          :disabled="
            isAutoStart ||
            round.finishTime ||
            round.startTime.toDate() <= minStartTime
          "
          @click="pushRounds(-pushStep)" />

        <b-button
          type="is-info"
          :label="`+${pushStep}m`"
          :disabled="round.finishTime || isAutoStart"
          @click="pushRounds(pushStep)"
      /></b-timepicker>

      <b-button
        type="is-static"
        :class="
          round.roundNumber <= tournament.currentRound
            ? ''
            : round.startTime && round.startTime.toDate() < now
            ? 'has-text-danger'
            : 'has-text-info'
        "
        :label="roundTimeText"
        style="border: none; width: 330px"
      />
    </b-field>

    <b-tooltip :label="roundGamesStats.text">
      <GamesProgressBar
        style="min-width: 150px"
        :gamesStats="roundGamesStats"
      />
    </b-tooltip>

    <div class="is-flex is-align-items-center">
      <b-button
        style="width: 120px"
        type="is-primary"
        :label="`Games: ${roundGamesStats.total}`"
        v-visible="
          round.roundNumber === 1 ||
          round.roundNumber <= tournament.currentRound ||
          (previousRound && previousRound.finishTime)
        "
        @click="$emit('showRoundTable')"
      />
      <span style="display: inline-block; width: 150px">
        <b-button
          type="is-success"
          label="Start Round"
          v-if="
            round.roundNumber === tournament.currentRound + 1 &&
            (round.roundNumber === 1 || previousRound.finishTime)
          "
          @click="$emit('onStart', round.roundNumber)"
        />
        <b-button
          type="is-danger"
          :label="round.finishTime ? 'Unfinish Round' : 'Finish Round'"
          v-else-if="
            round.roundNumber === tournament.currentRound ||
            (round.roundNumber === tournament.totalRounds &&
              tournament.currentRound === tournament.totalRounds + 1)
          "
          @click="
            round.finishTime
              ? $emit('onUnfinish', round.roundNumber)
              : $emit('onFinish', round.roundNumber)
          "
        />
      </span>
    </div>
  </div>
</template>

<script>
import GamesProgressBar from "@/components/tournaments/GamesProgressBar.vue";
import GameHelper from "@/utils/game-helper";
import {
  setRoundStartTime,
  pushRounds,
} from "vue-chess-shared/tournament/helper";
import { formatDistance } from "vue-chess-shared/utils/date-helper";
import { firestore } from "@/services/firebase";

export default {
  components: { GamesProgressBar },

  name: "AdminRound",

  props: ["tournament", "rounds", "round", "now", "roundGames"],

  computed: {
    previousRound() {
      if (!this.round || !this.rounds) return null;
      if (this.round.roundNumber <= 1) return null;
      return this.rounds[this.round.roundNumber - 2];
    },

    nextRound() {
      if (!this.round || !this.rounds) return null;
      if (this.round.roundNumber >= this.tournament.totalRounds) return null;
      return this.rounds[this.round.roundNumber];
    },

    minStartTime() {
      if (!this.previousRound) return this.tournament.startTime.toDate();
      return new Date(
        this.previousRound.startTime.toMillis() +
          this.tournament.baseTimeControl * 2 * 60 * 1000
      );
    },

    roundGamesStats() {
      const roundGames = this.roundGames;
      const total = roundGames.filter((g) => g.whiteUsername).length;
      const bye = roundGames.filter(
        (g) => g.whiteUsername && !g.blackUsername
      ).length;
      const matches = roundGames.filter((g) => g.matchRef).length;
      const unfinished = roundGames.filter(
        (g) => g.matchRef && GameHelper.isUnfinished(g)
      ).length;
      const finished = roundGames.filter(
        (g) => g.matchRef && !GameHelper.isUnfinished(g)
      ).length;
      const bots = roundGames.filter(
        (g) =>
          g.whiteUsername &&
          g.blackUsername &&
          g.whiteUsername.startsWith("ai_") &&
          g.blackUsername.startsWith("ai_")
      ).length;
      const text = total
        ? `${bye} bye, ${finished} finished, ${unfinished} unfinished`
        : "0 game";
      return { total, bye, matches, unfinished, finished, bots, text };
    },

    pushStep() {
      if (!this.tournament) return 5;
      return Math.min(this.tournament.baseTimeControl, 5);
    },

    isAutoStart() {
      return (
        this.tournament.roundMode === "Auto" &&
        this.round.roundNumber > 1 &&
        !this.previousRound.finishTime
      );
    },

    roundTimeText() {
      if (!this.round || !this.round.startTime) return "";
      let s = "";
      if (this.isAutoStart) {
        s += `Starts in 30s after round ${this.round.roundNumber - 1} finishes`;
      } else if (this.now) {
        if (this.nextRound && this.nextRound.startTime) {
          s += `Duration: ${Math.round(
            (this.nextRound.startTime - this.round.startTime) / 60
          )} mins. `;
        } else if (this.round.finishTime) {
          s += `Duration: ${Math.round(
            (this.round.finishTime - this.round.startTime) / 60
          )} mins. `;
        }

        s +=
          (this.round.startTime.toDate() > this.now ? "Starts " : "Started ") +
          formatDistance(this.round.startTime, this.now);
      }
      return s;
    },
  },

  methods: {
    setRoundStartTime(date) {
      setRoundStartTime(
        firestore,
        this.tournament,
        this.rounds,
        this.round.roundNumber,
        date
      );
    },

    pushRounds(minutes) {
      pushRounds(
        firestore,
        this.tournament,
        this.rounds,
        this.round.roundNumber,
        minutes
      );
    },
  },
};
</script>

<style></style>
